import { Realtime } from "ably";
import { LiveObjects } from "ably/liveobjects";

const DEFAULT_CLIENT_PREFIX = "biyopos-terminal";

class LiveObjectsService {
  constructor () {
    this._client = null;
    this._maps = {}; // Cache for LiveMap instances
    this._initializing = null;
    this._context = null;
    this._isReady = false;
    this._connectionStateHandlers = [];
    this._syncingChannels = new Set();
  }

  // After a reconnect the SDK re-syncs each channel's objects; reading a map
  // before that finishes returns the pre-disconnect state. Bounded wait so a
  // stuck sync can't block reconciliation forever.
  async waitForMapSync (type, timeoutMs = 5000) {
    const channelName = this._getChannelName(type);
    if (!channelName || !this._syncingChannels.has(channelName) || !this._client) {
      return;
    }
    const channel = this._client.channels.get(channelName);
    await new Promise((resolve) => {
      const timer = setTimeout(() => {
        console.warn(`[LiveObjects] ${type} objects still syncing after ${timeoutMs}ms; using current state`);
        subscription.off();
        resolve();
      }, timeoutMs);
      const subscription = channel.object.on("synced", () => {
        clearTimeout(timer);
        subscription.off();
        resolve();
      });
    });
  }

  // Lets other modules (e.g. the realtime Vuex module) react to the Ably
  // connection dropping/recovering instead of only trusting the SDK's
  // internal retry silently.
  onConnectionStateChange (handler) {
    if (typeof handler === "function") {
      this._connectionStateHandlers.push(handler);
    }
  }

  _emitConnectionStateChange (stateChange) {
    this._connectionStateHandlers.forEach((handler) => {
      try {
        handler(stateChange);
      } catch (error) {
        console.warn("[LiveObjects] connection state handler failed", error);
      }
    });
  }

  getConnectionState () {
    return this._client && this._client.connection ? this._client.connection.state : "closed";
  }

  // Force a reconnect attempt if the connection has stalled outside of an
  // active connect/connected state (e.g. after the tab wakes from sleep).
  reconnectIfNeeded () {
    const state = this.getConnectionState();
    if (state !== "connected" && state !== "connecting") {
      console.log(`[LiveObjects] Forcing reconnect from state: ${state}`);
      if (this._client) {
        this._client.connection.connect();
      }
    }
  }

  async initialize ({ apiKey, storeId, storeSubdomain, terminalId }) {
    if (!apiKey) {
      console.warn("[LiveObjects] Missing Ably API key; realtime sync disabled.");
      return;
    }
    if (!storeId || !terminalId) {
      console.warn("[LiveObjects] Missing storeId or terminalId; skipping initialization.");
      return;
    }

    const normalizedSubdomain = (storeSubdomain || "store").toLowerCase();

    const previousContext = this._context;
    this._context = {
      apiKey,
      storeId,
      storeSubdomain: normalizedSubdomain,
      terminalId
    };

    if (previousContext && previousContext.storeId !== storeId) {
      this._teardown();
    }

    if (this._isReady && previousContext && previousContext.storeId === storeId) {
      return;
    }

    if (this._initializing) {
      return this._initializing;
    }

    this._initializing = this._bootClient()
      .then(() => {
        this._isReady = true;
        // One-glance sync diagnostic: this must print the SAME channel as the
        // matching log in biyo-order, or the two apps are not talking at all.
        console.log(`[LiveObjects] ready — orders channel: ${this._getChannelName('orders')} clientId: ${this.getClientId()}`);
      })
      .catch((error) => {
        console.error("[LiveObjects] Failed to initialize", error);
      })
      .finally(() => {
        this._initializing = null;
      });

    return this._initializing;
  }

  async _bootClient () {
    if (this._client) {
      return;
    }

    const { apiKey, terminalId } = this._context;
    const clientId = `${DEFAULT_CLIENT_PREFIX}-${terminalId}`;

    this._client = new Realtime({
      key: apiKey,
      clientId,
      autoConnect: true,
      plugins: { LiveObjects }
    });

    this._client.connection.on((stateChange) => {
      console.log(`[LiveObjects] connection state: ${stateChange.previous} -> ${stateChange.current}`);
      this._emitConnectionStateChange(stateChange);
    });
  }

  _getChannelName (type) {
    const { storeId, storeSubdomain } = this._context;
    if (!storeId) return null;
    if (type === 'product_86d') return `product_86d:${storeSubdomain}-${storeId}`;
    return `${storeSubdomain}-${storeId}-${type}`;
  }

  async _ensureMap (type) {
    if (!this._client || !this._context) {
      console.warn(`[LiveObjects] Cannot ensure map for "${type}" - client or context not available`);
      return null;
    }
    const channelName = this._getChannelName(type);
    if (!channelName) {
      console.warn(`[LiveObjects] Cannot ensure map for "${type}" - channel name is null`);
      return null;
    }

    if (this._maps[channelName]) {
      return this._maps[channelName];
    }

    try {
      // LiveObjects requires object_subscribe and object_publish modes
      const channelOptions = {
        modes: ['publish', 'subscribe', 'presence', 'object_subscribe', 'object_publish']
      };
      const channel = this._client.channels.get(channelName, channelOptions);
      channel.object.on("syncing", () => this._syncingChannels.add(channelName));
      channel.object.on("synced", () => this._syncingChannels.delete(channelName));
      // Get the root LiveMap for this channel
      const map = await channel.object.get();
      this._maps[channelName] = map;
      return map;
    } catch (error) {
      console.error(`[LiveObjects] Failed to get map for ${type}`, error);
      return null;
    }
  }

  // --- Orders ---

  async _ensureOrdersMap () {
    return this._ensureMap('orders');
  }

  async publishOrderUpdate (payload) {
    const map = await this._ensureOrdersMap();
    if (!map || !payload) return;

    const key = (payload.id || payload.local_id || "").toString();
    if (!key) {
      console.warn("[LiveObjects] Cannot publish order update - no ID found");
      return;
    }

    try {
      await map.set(key, payload);
    } catch (error) {
      console.error("[LiveObjects] Failed to publish order update", error);
      console.error("[LiveObjects] Failed to publish order update", {payload});
      console.error("[LiveObjects] Failed to publish order update", JSON.stringify({payload}));
    }
  }

  async publishOrderDeletion (payload) {
    const map = await this._ensureOrdersMap();
    if (!map || !payload) return;

    const key = (payload.id || payload.local_id || "").toString();
    if (!key) {
      console.warn("[LiveObjects] Cannot publish order deletion - no ID found");
      return;
    }

    try {
      await map.remove(key);
    } catch (error) {
      console.error("[LiveObjects] Failed to publish order deletion", error);
    }
  }

  _unwrapLiveNodeValue (node) {
    if (!node) return null;
    try {
      if (typeof node.value === "function") {
        return node.value();
      }
    } catch (error) {
      console.warn("[LiveObjects] Failed to unwrap live node value", error);
    }
    return node;
  }

  _normalizeMapSnapshotEntries (snapshot) {
    if (!snapshot) return [];

    if (Array.isArray(snapshot)) {
      return snapshot
          .map(item => this._unwrapLiveNodeValue(item))
          .filter(Boolean);
    }

    if (snapshot instanceof Map) {
      return Array.from(snapshot.values())
          .map(item => this._unwrapLiveNodeValue(item))
          .filter(Boolean);
    }

    if (typeof snapshot === "object") {
      return Object.values(snapshot)
          .map(item => this._unwrapLiveNodeValue(item))
          .filter(Boolean);
    }

    return [];
  }

  async getAllOrdersSnapshot () {
    const map = await this._ensureOrdersMap();
    if (!map) return [];
    await this.waitForMapSync("orders");

    try {
      if (typeof map.value === "function") {
        const snapshot = map.value();
        const entries = this._normalizeMapSnapshotEntries(snapshot);
        if (entries.length > 0) {
          return entries;
        }
      }

      if (typeof map.entries === "function") {
        const entries = Array.from(map.entries())
            .map(([, value]) => this._unwrapLiveNodeValue(value))
            .filter(Boolean);
        if (entries.length > 0) {
          return entries;
        }
      }
    } catch (error) {
      console.error("[LiveObjects] Failed to read orders snapshot", error);
    }

    console.warn("[LiveObjects] Orders snapshot unavailable from map; returning empty set");
    return [];
  }

  async subscribeToOrderUpdates (callback) {
    const map = await this._ensureOrdersMap();
    if (!map) return () => {};

    const listener = (event) => {
        if (typeof callback !== "function") return;

        // After a (re)attach the SDK re-syncs the whole map and notifies with
        // no message — the changed keys aren't exposed. Signal the caller so
        // it can re-apply the map to its local orders; this is how changes
        // made while this terminal was disconnected arrive.
        if (!event.message) {
            console.log("[LiveObjects] orders map re-synced");
            callback({ synced: true });
            return;
        }

        const op = event.message.operation;
        // Look for map.set operations
        if (op && op.action === 'map.set' && op.mapOp) {
            const key = op.mapOp.key;
            // Get the value from the map instance
            const data = map.get(key).value();
            callback({ data });
        }
    };

    try {
        const subscription = await map.subscribe(listener);
        return () => {
            subscription.unsubscribe();
        };
    } catch (e) {
        console.error("[LiveObjects] Failed to subscribe to order updates", e);
        return () => {};
    }
  }

  async subscribeToOrderDeletion (callback) {
    const map = await this._ensureOrdersMap();
    if (!map) return () => {};

    const listener = (event) => {
        const op = event.message?.operation;
        if (op && op.action === 'map.remove' && op.mapOp) {
            const key = op.mapOp.key;
            const clientId = event.message.clientId;

            const payload = {
                id: key,
                local_id: key,
                source: clientId
            };

            if (typeof callback === "function") {
                callback({ data: payload });
            }
        }
    };

    try {
        const subscription = await map.subscribe(listener);
        return () => {
            subscription.unsubscribe();
        };
    } catch (e) {
        console.error("[LiveObjects] Failed to subscribe to order deletions", e);
        return () => {};
    }
  }

  // --- Tables ---

  async _ensureTablesMap () {
    return this._ensureMap('tables');
  }

  async publishTableUpdate (payload) {
    const map = await this._ensureTablesMap();
    if (!map || !payload) return;
    const key = (payload.id || "").toString();
    if (!key) return;
    try {
      await map.set(key, payload);
    } catch (error) {
      console.error("[LiveObjects] Failed to publish table update", error);
    }
  }

  async publishTablesReplace (payload) {
    // This was previously an event. Now we treat it as batch update.
    if (!Array.isArray(payload)) return;
    const map = await this._ensureTablesMap();
    if (!map) return;

    try {
      await map.batch((batch) => {
        payload.forEach(table => {
           const key = (table.id || "").toString();
           if (key) batch.set(key, table);
        });
      });
    } catch (error) {
      console.error("[LiveObjects] Failed to publish tables replace", error);
    }
  }

  async subscribeToTableUpdates (callback) {
    const map = await this._ensureTablesMap();
    if (!map) return () => {};

    const listener = (event) => {
        const op = event.message?.operation;
        if (op && op.action === 'map.set' && op.mapOp) {
            const key = op.mapOp.key;
            const data = map.get(key).value();
            if (typeof callback === "function") callback({ data });
        }
    };
    try {
        const subscription = await map.subscribe(listener);
        return () => subscription.unsubscribe();
    } catch(e) { return () => {}; }
  }

  async subscribeToTablesReplace () {
     // LiveMap handles sync automatically. We can probably ignore this or map it to updates.
     return () => {};
  }

  // --- Sections ---

  async _ensureSectionsMap () {
    return this._ensureMap('sections');
  }

  async publishSectionsUpdate (payload) {
    const map = await this._ensureSectionsMap();
    if (!map || !payload) return;
    const key = (payload.id || "").toString();
    if (!key) return;
    try {
      await map.set(key, payload);
    } catch (error) {
      console.error("[LiveObjects] Failed to publish sections update", error);
    }
  }

  async subscribeToSectionsUpdate (callback) {
    const map = await this._ensureSectionsMap();
    if (!map) return () => {};

    const listener = (event) => {
        const op = event.message?.operation;
        if (op && op.action === 'map.set' && op.mapOp) {
            const key = op.mapOp.key;
            const data = map.get(key).value();
            if (typeof callback === "function") callback({ data });
        }
    };
    try {
        const subscription = await map.subscribe(listener);
        return () => subscription.unsubscribe();
    } catch (e) { return () => {}; }
  }

  // --- Order Presence (for locking) ---

  _getOrderPresenceChannel (orderId) {
    if (!this._client || !this._context || !orderId) return null;
    const { storeId, storeSubdomain } = this._context;
    const channelName = `${storeSubdomain}-${storeId}-order-presence-${orderId}`;
    return this._client.channels.get(channelName);
  }

  async enterOrderPresence (orderId) {
    const channel = this._getOrderPresenceChannel(orderId);
    if (!channel) return;

    try {
      const { terminalId } = this._context;
      await channel.presence.enter({ terminalId, orderId, enteredAt: Date.now() });
    } catch (error) {
      console.error(`[LiveObjects] Failed to enter presence for order ${orderId}`, error);
    }
  }

  async leaveOrderPresence (orderId) {
    const channel = this._getOrderPresenceChannel(orderId);
    if (!channel) return;

    try {
      await channel.presence.leave();
    } catch (error) {
      console.error(`[LiveObjects] Failed to leave presence for order ${orderId}`, error);
    }
  }

  async getOrderPresenceMembers (orderId) {
    const channel = this._getOrderPresenceChannel(orderId);
    if (!channel) return [];

    try {
      const members = await channel.presence.get();
      return members.map(m => ({
        clientId: m.clientId,
        terminalId: m.data?.terminalId,
        orderId: m.data?.orderId,
        enteredAt: m.data?.enteredAt
      }));
    } catch (error) {
      console.error(`[LiveObjects] Failed to get presence for order ${orderId}`, error);
      return [];
    }
  }

  async isOrderBeingViewedByOther (orderId, excludeTerminalId) {
    const members = await this.getOrderPresenceMembers(orderId);
    return members.some(m => m.terminalId && m.terminalId !== excludeTerminalId);
  }

  async getTerminalViewingOrder (orderId, excludeTerminalId) {
    const members = await this.getOrderPresenceMembers(orderId);
    const otherMember = members.find(m => m.terminalId && m.terminalId !== excludeTerminalId);
    return otherMember ? otherMember.terminalId : null;
  }

  // --- Shifts ---

  async _ensureShiftsMap () {
    return this._ensureMap('shifts');
  }

  async publishShiftUpdate (payload) {
    const map = await this._ensureShiftsMap();
    if (!map || !payload) return;
    const key = (payload.id || "").toString();
    if (!key) return;
    try {
      await map.set(key, payload);
    } catch (error) {
      console.error("[LiveObjects] Failed to publish shift update", error);
    }
  }

  async subscribeToShiftUpdate (callback) {
    const map = await this._ensureShiftsMap();
    if (!map) return () => {};

    const listener = (event) => {
        const op = event.message?.operation;
        if (op && op.action === 'map.set' && op.mapOp) {
            const key = op.mapOp.key;
            const data = map.get(key).value();
            if (typeof callback === "function") callback({ data });
        }
    };
    try {
        const subscription = await map.subscribe(listener);
        return () => subscription.unsubscribe();
    } catch (e) { return () => {}; }
  }

  // --- Product 86 (out of stock) — channel: product_86d:{subdomain}-{storeId} ---
  // Map keys: "<productId>" = whole product; "pm:<productId>:<modifierOptionId>" = modifier option for that product

  _buildProduct86dMapKey (payload) {
    if (!payload || payload.product_id == null) return null;
    const pid = String(payload.product_id);
    if (payload.modifier_option_id != null && payload.modifier_option_id !== "") {
      return `pm:${pid}:${String(payload.modifier_option_id)}`;
    }
    return pid;
  }

  _parseProduct86dMapKey (key) {
    const s = String(key);
    if (s.startsWith("pm:")) {
      const parts = s.split(":");
      if (parts.length >= 3) {
        return {
          product_id: parts[1],
          modifier_option_id: parts.slice(2).join(":"),
          is_86d: false
        };
      }
    }
    return { product_id: s, modifier_option_id: null, is_86d: false };
  }

  async _ensureProduct86dMap () {
    return this._ensureMap('product_86d');
  }

  async publishProduct86d (payload) {
    const map = await this._ensureProduct86dMap();
    if (!map || !payload) return;

    const key = this._buildProduct86dMapKey(payload);
    if (!key) {
      console.warn("[LiveObjects] Cannot publish product 86d - no product_id");
      return;
    }

    try {
      if (payload.is_86d) {
        await map.set(key, payload);
      } else {
        await map.remove(key);
      }
    } catch (error) {
      console.error("[LiveObjects] Failed to publish product 86d", error);
    }
  }

  async getAllProduct86dSnapshot () {
    const map = await this._ensureProduct86dMap();
    if (!map) return [];

    try {
      if (typeof map.value === "function") {
        const snapshot = map.value();
        const entries = this._normalizeMapSnapshotEntries(snapshot);
        if (entries.length > 0) {
          return entries;
        }
      }

      if (typeof map.entries === "function") {
        const entries = Array.from(map.entries())
            .map(([, value]) => this._unwrapLiveNodeValue(value))
            .filter(Boolean);
        if (entries.length > 0) {
          return entries;
        }
      }
    } catch (error) {
      console.error("[LiveObjects] Failed to read product 86d snapshot", error);
    }

    return [];
  }

  async subscribeToProduct86dSet (callback) {
    const map = await this._ensureProduct86dMap();
    if (!map) return () => {};

    const listener = (event) => {
      const op = event.message?.operation;
      if (op && op.action === 'map.set' && op.mapOp) {
        const key = op.mapOp.key;
        let data;
        try {
          const node = map.get(key);
          data = node && typeof node.value === "function" ? node.value() : null;
        } catch (e) {
          data = null;
        }
        if (typeof callback === "function") {
          callback({ data });
        }
      }
    };

    try {
      const subscription = await map.subscribe(listener);
      return () => subscription.unsubscribe();
    } catch (e) {
      console.error("[LiveObjects] Failed to subscribe to product 86d updates", e);
      return () => {};
    }
  }

  async subscribeToProduct86dRemove (callback) {
    const map = await this._ensureProduct86dMap();
    if (!map) return () => {};

    const listener = (event) => {
      const op = event.message?.operation;
      if (op && op.action === 'map.remove' && op.mapOp) {
        const key = op.mapOp.key;
        const parsed = this._parseProduct86dMapKey(key);
        const payload = {
          product_id: parsed.product_id,
          modifier_option_id: parsed.modifier_option_id,
          is_86d: false,
          source: event.message?.clientId
        };
        if (typeof callback === "function") {
          callback({ data: payload });
        }
      }
    };

    try {
      const subscription = await map.subscribe(listener);
      return () => subscription.unsubscribe();
    } catch (e) {
      console.error("[LiveObjects] Failed to subscribe to product 86d removals", e);
      return () => {};
    }
  }

  // Subscribes to real-time auto_86d_update messages published by the backend via Ably REST.
  async subscribeToBackendProduct86dUpdate (callback) {
    if (!this._client || !this._context) return () => {};
    const channelName = this._getChannelName('product_86d');
    if (!channelName) return () => {};
    try {
      const channel = this._client.channels.get(channelName);
      const listener = (message) => {
        console.log('[LiveObjects] auto_86d_update received live ts=', message.timestamp, message.data);
        if (typeof callback === 'function') callback({ data: message.data, timestamp: message.timestamp });
      };
      console.log('[LiveObjects] subscribing to auto_86d_update on channel=', channelName);
      await channel.subscribe('auto_86d_update', listener);
      return () => channel.unsubscribe('auto_86d_update', listener);
    } catch (e) {
      console.error('[LiveObjects] Failed to subscribe to backend product 86d updates', e);
      return () => {};
    }
  }

  // Fetches any auto_86d_update messages missed while offline, oldest-first.
  // Requires "Persist all messages" enabled on the product_86d namespace in the Ably dashboard.
  async getMissedProduct86dMessages () {
    if (!this._client || !this._context) {
      console.warn('[LiveObjects] getMissedProduct86dMessages: no client or context');
      return [];
    }
    const channelName = this._getChannelName('product_86d');
    console.log('[LiveObjects] getMissedProduct86dMessages channel=', channelName);
    if (!channelName) return [];
    try {
      const channel = this._client.channels.get(channelName);
      console.log('[LiveObjects] getMissedProduct86dMessages channel.state=', channel.state);
      const page = await channel.history({ untilAttach: true });
      console.log('[LiveObjects] getMissedProduct86dMessages raw items=', page.items.length, page.items.map(m => ({ name: m.name, data: m.data, ts: new Date(m.timestamp).toISOString() })));
      // page.items is newest-first — take the first occurrence of each product_id
      // so we apply only the most recent state, not the full replay of every toggle
      const seen = new Set();
      const latest = [];
      for (const msg of page.items) {
        if (msg.name !== 'auto_86d_update') continue;
        const pid = String(msg.data?.product_id ?? '');
        if (!pid || seen.has(pid)) continue;
        seen.add(pid);
        latest.push(msg);
      }
      console.log('[LiveObjects] getMissedProduct86dMessages deduped=', latest.map(m => ({ product_id: m.data.product_id, is_86d: m.data.is_86d, ts: new Date(m.timestamp).toISOString() })));
      return latest;
    } catch (e) {
      console.warn('[LiveObjects] getMissedProduct86dMessages failed', e);
      return [];
    }
  }

  isReady () {
    return this._isReady;
  }

  getClientId () {
    if (this._client && this._client.auth) {
      return this._client.auth.clientId;
    }
    return null;
  }

  _teardown () {
    if (this._client) {
      try {
        this._client.close();
      } catch (error) {
        console.warn("[LiveObjects] Failed to close client", error);
      }
    }
    this._client = null;
    this._maps = {};
    this._syncingChannels = new Set();
    this._isReady = false;
  }
}

const liveObjectsService = new LiveObjectsService();
export default liveObjectsService;
